// Copyright (c) Docugami, Inc. All rights reserved.

import { dgFontFamily, userFontFamily } from "components/common/Fonts";
import { homeGray, primaryTheme } from "components/shell/presentation/AppStyles";
import { DefaultButton, DirectionalHint, IButtonStyles, IconButton, IOverflowSetItemProps, OverflowSet, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import React, { useState } from "react";
import styled from "styled-components";
import { IconDescriptor, IconRenderer } from "./IconRenderer";
import { DgMultineEllipsis } from "components/common/DgCommonStyledComponents";

const ClickableContainerItem = styled.div`
    width: 120px;
    padding: 10px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
`;

const TextContainer = styled(DgMultineEllipsis)`
    text-align: center;
    margin-top: 5px;
    width: 100%;
    text-overflow: ellipsis;
`

const WorkPathItemName = styled.span`
    font-family: ${userFontFamily};
    font-weight: 400;
    font-size: 14px;
`;

export interface WorkPathItemProps {
    iconProps: IconDescriptor;
    title: string;
    clickHandler: () => void;
    menuProps?: WorkPathOverflowMenuProps;
    iconHeight: number;
}

/**
 * Visual representation of a work path in the viewer
 * @param props
 */
export const WorkPathItem = (props: WorkPathItemProps): JSX.Element => {
    const [isHovered, setIsHovered] = useState(false);

    const iconWidth = props.iconHeight * props.iconProps.width

    return (
        <ClickableContainerItem onClick={() => props.clickHandler()} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} >
            {!!props.menuProps && <div style={{height: "max-content", width: "100%"}}>
                <WorkPathOverflowMenu hidden={!isHovered} {...props.menuProps} />
            </div> }
            <IconRenderer source={props.iconProps} height={props.iconHeight} width={iconWidth} containerHeight={props.iconHeight} containerWidth={iconWidth} />
            <TextContainer numLines={2}>
                <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={props.title} directionalHint={DirectionalHint.bottomCenter}>
                    <WorkPathItemName>{props.title}</WorkPathItemName>
                </TooltipHost>
            </TextContainer>
        </ClickableContainerItem>
    );
}

const OverflowMenu = styled(OverflowSet)`
    width: 40px;
    margin-left: auto;
    span {
        color: ${homeGray};
    }
    i {
        font-weight: 800;
        color: ${primaryTheme.palette.black};
    }
    button {
        padding-bottom: 2px;
        width: 30px;
        height: 30px;
        font-weight: 300;
        font-size: 20px;
    }
`;

const MoreButton = styled(DefaultButton)`
    border: none;
    font-family: ${dgFontFamily};
    font-size: 18px;
    i {
        display: none;
    }
`;

export interface WorkPathOverflowMenuProps {
    hidden?: boolean;
    smallIcon?: boolean;
    renameEnabled: boolean;
    deleteEnabled: boolean;
    copyEnabled: boolean;
    onRenameClicked: () => void;
    onDeleteClicked: () => void;
    onCopyClicked: () => void;
}

export const WorkPathOverflowMenu = (props: WorkPathOverflowMenuProps): JSX.Element => {
    function onRenderOverflowButton(overflowItems: any[] | undefined): JSX.Element {
        const buttonStyles: Partial<IButtonStyles> = {
            root: {
                minWidth: 0,
                padding: '0 1px',
                alignSelf: 'stretch',
                height: 'auto',
                visibility: !props.hidden ? 'visible' : 'hidden'
            },
        };

        if (!!props.smallIcon) {
            return (
                <IconButton
                    iconProps={{ iconName: "MoreVertical" }}
                    styles={{
                        icon: { color: primaryTheme.palette.black },
                        rootHovered: { backgroundColor: primaryTheme.palette.neutralLighterAlt }
                    }}
                    menuProps={{ items: overflowItems!, styles: { root: { backgroundColor: primaryTheme.palette.white } } }}
                    onRenderMenuIcon={() => null} />
            );
        }

        return (
            <MoreButton
                role="menuitem"
                title="More options"
                styles={buttonStyles}
                text="&bull;&bull;&bull;"
                menuProps={{ items: overflowItems!, styles: { root: { backgroundColor: primaryTheme.palette.white } } }}
            />
        );
    };

    const overflowItems: IOverflowSetItemProps[] = [
        {
            key: "rename",
            name: "Rename",
            onClick: () => props.onRenameClicked(),
            disabled: !props.renameEnabled
        },
        {
            key: "delete",
            name: "Delete",
            onClick: () => props.onDeleteClicked(),
            disabled: !props.deleteEnabled
        },
        {
            key: "copy",
            name: "Copy",
            onClick: props.onCopyClicked,
            disabled: !props.copyEnabled
        }
    ];

    return <OverflowMenu styles={{root: { height: "100%", alignItems: "center" }}} overflowItems={overflowItems} onRenderItem={() => { }} onRenderOverflowButton={onRenderOverflowButton} />
}
